import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Loader2 } from "lucide-react";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import useMediaQuery from '@mui/material/useMediaQuery';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const LoadingSpinner = React.memo(() => (
  <div className="flex justify-center items-center p-4">
    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
  </div>
));

const MemoizedActionBar = memo(ActionBar);
const MemoizedDynamicTable = memo(DynamicTable);

const Indent2 = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownData, setDropdownData] = useState({
    projects: [],
    types: [],
    users: []
  });
  
  // Add ref to track if data has been fetched
  const hasFetchedData = useRef(false);
  const rowsPerPage = 10;

  const navigate = useNavigate();
  
  // Memoize API endpoint
  const apiEndpoint = useMemo(() => `${API_BASE_URL}/indent/indent`, []);

  const authState = useSelector((state) => state.auth);
  const { role, locationid, isAuthenticated, isAuthorized } = authState;
  const id = authState.id || localStorage.getItem("id");

  const getApiEndpoint = useCallback(() => {
    return role === "admin"
      ? `${API_BASE_URL}/adminpage/l1indent`
      : `${API_BASE_URL}/l1page/l1indent`;
  }, [role]);

  const isMobile = useMediaQuery('(max-width: 640px)');

  // Memoize formatCreationDate function
  const formatCreationDate = useCallback((dateStr) => {
    if (!dateStr) return "N/A";
    if (/^\d{2}[\/-]\d{2}[\/-]\d{2,4}$/.test(dateStr)) return dateStr;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }, []);

  // Fetch indents implementation
  const fetchIndents = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const endpoint = getApiEndpoint();
      const queryParams = role === "admin" ? {} : { UserId: id };

      const response = await axios.get(endpoint, {
        params: queryParams,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      const indentsData = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];

      if (!indentsData.length) {
        setError("No indents found for this location");
        setIndents([]);
        setFilteredIndents([]);
        return;
      }

      const formattedIndents = indentsData.map((indent) => ({
        ...indent,
        CreationDate: formatCreationDate(indent.CreationDate),
      }));

      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
      setCurrentPage(1);
    } catch (error) {
      setError(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch indents"
      );
      setIndents([]);
      setFilteredIndents([]);
    } finally {
      setIsLoading(false);
    }
  }, [getApiEndpoint, id, role, formatCreationDate]);

  // Fetch dropdown data implementation
  const fetchDropdownData = useCallback(async () => {
    try {
      const [projectsRes, typesRes, usersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/projects`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }),
        axios.get(`${API_BASE_URL}/types`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }),
        axios.get(`${API_BASE_URL}/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
      ]);

      setDropdownData({
        projects: projectsRes.data || [],
        types: typesRes.data || [],
        users: usersRes.data || []
      });
    } catch (error) {
      console.error("Failed to fetch dropdown data:", error);
      // Set empty arrays to prevent undefined errors
      setDropdownData({ projects: [], types: [], users: [] });
    }
  }, []);

  // Memoize the conditions for data fetching
  const shouldFetchData = useMemo(() => {
    return role &&
      (role === "admin" || role === "Location Head") &&
      isAuthenticated &&
      isAuthorized &&
      localStorage.getItem("token");
  }, [role, isAuthenticated, isAuthorized]);

  useEffect(() => {
    if (shouldFetchData && !hasFetchedData.current) {
      Promise.all([fetchIndents(), fetchDropdownData()]);
      hasFetchedData.current = true;
    } else if (!shouldFetchData) {
      console.warn("Data Fetching Conditions Not Met", {
        roleCheck: !!role,
        roleType: role,
        authCheck: isAuthenticated,
        authorizedCheck: isAuthorized,
        tokenCheck: !!localStorage.getItem("token"),
      });
      setIsLoading(false);
    }
  }, [shouldFetchData, fetchIndents, fetchDropdownData]);

  const handleRowClick = useCallback((row) => {
    navigate(`/viewTable`, {
      state: {
        indentId: row.IndentID,
        indentDetails: row,
        row: row,
      },
    });
  }, [navigate]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      setFilteredIndents(indents);
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
    const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.ProjectName,
        indent.TypeName,
        indent.Status,
        indent.CreationDate,
        indent.TaskSchedule,
        indent.L1,
        indent.L2,
        indent.PO,
        indent.PH,
        indent.IR,
        indent.SH
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
      );
    });
    
    setFilteredIndents(filtered);
    setCurrentPage(1);
  }, [indents]);

  const handleDateFilter = useCallback((filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (!dateFrom || !dateTo) {
      setFilteredIndents(indents);
      return;
    }

    const filtered = indents.filter((indent) => {
      const creationDate = new Date(indent.CreationDate);
      return creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo);
    });
    setFilteredIndents(filtered);
    setCurrentPage(1);
  }, [indents]);

  // Memoize the refresh handler
  const handleRefresh = useCallback(() => {
    fetchIndents();
  }, [fetchIndents]);

  const editFields = useMemo(() => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options: dropdownData.projects?.map(p => ({
        value: p.id,
        label: p.name
      })) || [],
      className: "w-full"
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options: dropdownData.types?.map(t => ({
        value: t.id,
        label: t.name
      })) || [],
      className: "w-full"
    }
  ], [dropdownData.projects, dropdownData.types]);

  // Memoize table columns
  const columns = useMemo(() => [
    { key: "IndentID", label: "Indent ID" },
    { key: "CreationDate", label: "Creation Date" },
    { key: "ProjectName", label: "Project" },
    { key: "TypeName", label: "Indent Type" },
    { key: "TaskSchedule", label: "Task Scheduled" },
    { key: "Status", label: "Status" },
    { key: "ItemCount", label: "Item Count" }
  ], []);

  const displayData = useMemo(() => {
    if (!filteredIndents) return [];
    return filteredIndents;
  }, [filteredIndents]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center p-4">
          <p className="text-red-500">{error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <div className="flex-grow p-2 sm:p-4">
        <div className="mb-4 text-center">
          <h3 className="text-xl font-semibold">View Table</h3>
        </div>

        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <MemoizedActionBar
              title="Indent"
              fields={editFields}
              apiEndpoint={apiEndpoint}
              data={displayData}
              selectedRow={selectedRow}
              onRowSelect={setSelectedRow}
              onRefresh={handleRefresh}
              onSearch={handleSearch}
              onFilter={handleDateFilter}
              rowKey="IndentID"
              userRole={role}
              addLink="/addIndent"
              editRoute="/viewTable"
              searchQuery={searchQuery}
            />
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <MemoizedDynamicTable
                columns={columns}
                data={displayData}
                selectedRow={selectedRow}
                onRowSelect={setSelectedRow}
                onDoubleClick={handleRowClick}
                rowKey="IndentID"
                userIdKey="UserID"
                highlightColor="bg-blue-100"
                noDataMessage="No indents found."
                userRole={role}
                mobileView={isMobile}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default memo(Indent2);